import { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { FaSort, FaSortUp, FaSortDown, FaSearch, FaInbox, FaColumns, FaFileCsv, FaFileExcel, FaFilePdf, FaPrint, FaDownload, FaChevronRight, FaCompress, FaExpand, FaTimes, FaLock, FaArrowUp, FaFileImport } from 'react-icons/fa';
import { EmptyState, SkeletonRow } from '../Kit/Feedback';
import { Pagination } from '../Kit/Navigation';
import { onExportCsv, onExportExcel, onExportPdf, onPrintTable } from './_tableExport';
import { onResolveTableTemplate } from './_TableTemplates';
import { TablePerPage, PER_PAGE_OPTIONS } from './TablePerPage';
import { TableViewToggle } from './TableViewToggle';
import { TableGrid } from './TableGrid';
import { TABLE_TEMPLATE_EVENT, onReadTableTemplate } from '../../AppTemplateResolved';

const BLOCK = 'LStudio__app_table';

/*
    🔥 TABLA DE DATOS

    Orden, búsqueda, paginación, selección, columnas ocultables, filas
    expandibles, filas bloqueadas, densidad, 16 variantes, vista en
    lista o grilla, y exportación a Excel, CSV, PDF e impresión.

    ⚠️ DOS MODOS DE DATOS

    isServerSide = false (default): ordena, filtra y pagina en memoria.
    isServerSide = true: solo AVISA lo que el usuario pidió.

    Mezclarlos es el error clásico: paginar en el cliente sobre datos
    ya paginados por el servidor muestra una sola página real.

    ⚠️ DOS FORMAS DE VER

    LISTA para comparar —columnas alineadas, muchos registros—.
    GRILLA para reconocer —tarjetas, menos registros pero más claros—.

    ⚠️ LA PAGINACIÓN PUEDE IR ARRIBA, ABAJO O EN LAS DOS

    Con listados largos, tener el selector solo abajo obliga a
    scrollear hasta el final para cambiar de página. `both` es lo
    cómodo en tablas de más de 20 filas.
*/

const onGetValue = (row, key) => (
    key.split('.').reduce((acc, part) => acc?.[part], row)
);

const onNormalize = (value) => (
    String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
);

export const DataTable = ({
    columns = [],
    rows = [],
    rowKey = 'id',
    title = 'Listado',

    variant = null,

    isLoading = false,
    isServerSide = false,

    // orden
    sortBy = null, sortDir = 'asc', onSortChange = null,

    // búsqueda
    isSearchable = true, searchPlaceholder = 'Buscar…',
    searchValue = null, onSearchChange = null,

    // paginación
    isPaginated = true, perPage = 12, onPerPageChange = null,
    page = null, totalItems = null, onPageChange = null,
    perPageOptions = PER_PAGE_OPTIONS,
    paginationPosition = 'both',          // bottom | top | both

    // vista
    view = null,                          // null = el usuario elige
    defaultView = 'list',
    hasViewToggle = true,
    renderGridItem = null,
    gridColumns = 3,

    // selección
    isSelectable = false, selected = [], onSelectChange = null,
    isRowSelectable = null,
    lockedTooltip = 'Registro del sistema, no se puede seleccionar',

    renderExpanded = null,

    // herramientas
    isExportable = true, exportFileName = 'listado',
    isColumnToggle = true, isDensityToggle = true,
    onImportClick = null,
    toolbar = null,
    toolbarPanel = null,
    customTools = null,

    // acciones
    onRowClick = null,
    rowActions = null,
    bulkActions = null,

    emptyTitle = 'No hay registros',
    emptyDescription = 'Cuando haya datos, aparecen acá.',
    emptyAction = null,

    hasScrollTop = false,
    isStriped = false,
    isSticky = true,
    isStickyFirst = false,
}) => {

    const REF__useRefMenus = useRef(null);

    const [useVariantId, setVariantId] = useState(() => variant ?? onReadTableTemplate());
    const [useLocalSort, setLocalSort] = useState({ by: sortBy, dir: sortDir });
    const [useLocalSearch, setLocalSearch] = useState('');
    const [useLocalPage, setLocalPage] = useState(1);
    const [useLocalPerPage, setLocalPerPage] = useState(perPage);
    const [useLocalView, setLocalView] = useState(defaultView);
    const [useDensity, setDensity] = useState(null);
    const [useHidden, setHidden] = useState([]);
    const [useExpanded, setExpanded] = useState([]);
    const [useMenu, setMenu] = useState(null);
    const [useShowTop, setShowTop] = useState(false);

    // 🔥 Escucha el cambio de variante desde el panel de desarrollo
    useEffect(() => {

        if (variant) return;

        const onChangeVariant = (event) => setVariantId(event.detail?.id);

        window.addEventListener(TABLE_TEMPLATE_EVENT, onChangeVariant);
        return () => window.removeEventListener(TABLE_TEMPLATE_EVENT, onChangeVariant);

    }, [variant]);

    const TEMPLATE = onResolveTableTemplate(variant ?? useVariantId);

    const SORT = isServerSide ? { by: sortBy, dir: sortDir } : useLocalSort;
    const SEARCH = searchValue ?? useLocalSearch;
    const PAGE = page ?? useLocalPage;
    const PER_PAGE = onPerPageChange ? perPage : useLocalPerPage;
    const VIEW = view ?? useLocalView;
    const DENSITY = useDensity ?? TEMPLATE.density ?? 'md';

    const IS_ALL = Number(PER_PAGE) === -1;

    // 🔥 Cierra los menús al clickear afuera
    useEffect(() => {

        const onClickOutside = (event) => {
            if (REF__useRefMenus.current && !REF__useRefMenus.current.contains(event.target)) setMenu(null);
        };

        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);

    }, []);

    /* 🔥 Volver arriba: aparece pasada la mitad del scroll */
    useEffect(() => {

        if (!hasScrollTop) return;

        const onScroll = () => {
            const TOTAL = document.documentElement.scrollHeight - window.innerHeight;
            setShowTop(TOTAL > 0 && window.scrollY > TOTAL / 2);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();

        return () => window.removeEventListener('scroll', onScroll);

    }, [hasScrollTop]);

    const MEMO__VISIBLE_COLUMNS = useMemo(() => (
        columns.filter((col) => !useHidden.includes(col.key))
    ), [columns, useHidden]);

    // ---------------------------- 🔥 FILTRADO LOCAL
    const MEMO__FILTERED = useMemo(() => {

        if (isServerSide || !SEARCH) return rows;

        const QUERY = onNormalize(SEARCH);

        return rows.filter((row) => (
            columns.some((col) => {
                const VALUE = col.searchValue ? col.searchValue(row) : onGetValue(row, col.key);
                return onNormalize(VALUE).includes(QUERY);
            })
        ));

    }, [rows, columns, SEARCH, isServerSide]);

    // ---------------------------- 🔥 ORDEN LOCAL
    const MEMO__SORTED = useMemo(() => {

        if (isServerSide || !SORT.by) return MEMO__FILTERED;

        const COLUMN = columns.find((c) => c.key === SORT.by);

        return [...MEMO__FILTERED].sort((a, b) => {

            const A = COLUMN?.sortValue ? COLUMN.sortValue(a) : onGetValue(a, SORT.by);
            const B = COLUMN?.sortValue ? COLUMN.sortValue(b) : onGetValue(b, SORT.by);

            if (A === B) return 0;

            // Los vacíos van siempre al final, sin importar la dirección
            if (A === null || A === undefined || A === '') return 1;
            if (B === null || B === undefined || B === '') return -1;

            const RESULT = typeof A === 'number' && typeof B === 'number'
                ? A - B
                : String(A).localeCompare(String(B), 'es', { numeric: true });

            return SORT.dir === 'asc' ? RESULT : -RESULT;
        });

    }, [MEMO__FILTERED, SORT, columns, isServerSide]);

    // ---------------------------- 🔥 PAGINACIÓN LOCAL
    const TOTAL = totalItems ?? MEMO__SORTED.length;

    const TOTAL_PAGES = (isPaginated && !IS_ALL)
        ? Math.max(1, Math.ceil(TOTAL / PER_PAGE))
        : 1;

    // Los filtros externos pueden reducir la cantidad de páginas sin pasar
    // por onSearch. Evita quedar parado en una página que ya no existe.
    useEffect(() => {
        if (isServerSide || page !== null || PAGE <= TOTAL_PAGES) return;
        setLocalPage(1);
    }, [PAGE, TOTAL_PAGES, isServerSide, page]);

    const MEMO__VISIBLE = useMemo(() => {

        if (isServerSide || !isPaginated || IS_ALL) return MEMO__SORTED;

        const START = (PAGE - 1) * PER_PAGE;

        return MEMO__SORTED.slice(START, START + PER_PAGE);

    }, [MEMO__SORTED, PAGE, PER_PAGE, isPaginated, isServerSide, IS_ALL]);

    // ---------------------------- 🔥 HANDLERS
    const onSort = (key) => {

        const NEXT_DIR = SORT.by === key && SORT.dir === 'asc' ? 'desc' : 'asc';

        if (isServerSide) return onSortChange?.(key, NEXT_DIR);

        setLocalSort({ by: key, dir: NEXT_DIR });
    };

    const onSearch = (event) => {

        const VALUE = event.target.value;

        // Al cambiar la búsqueda siempre volvemos a la primera página.
        // Si la búsqueda está controlada por la vista, antes se mantenía
        // la página anterior y podía mostrar un falso "Sin resultados".
        setLocalPage(1);

        if (onSearchChange) return onSearchChange(VALUE);

        setLocalSearch(VALUE);
    };

    const onPage = (next) => {
        if (onPageChange) return onPageChange(next);
        setLocalPage(next);
    };

    const onPerPage = (next) => {

        if (onPerPageChange) return onPerPageChange(next);

        setLocalPerPage(next);
        setLocalPage(1);   // 25 por página en la página 9 no existiría
    };

    const onView = (next) => setLocalView(next);

    const onToggleColumn = (key) => setHidden((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));

    const onToggleExpand = (key) => setExpanded((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));

    const onToggleRow = (key) => onSelectChange?.(
        selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key],
    );

    /*
        🔥 FILAS BLOQUEADAS
        ⚠️ "Seleccionar todos" tiene que IGNORARLAS. Si las contara, el
           check de la cabecera nunca se marcaría del todo.
    */
    const onIsSelectable = (row) => (isRowSelectable ? isRowSelectable(row) : true);

    const VISIBLE_KEYS = MEMO__VISIBLE.filter(onIsSelectable).map((row) => onGetValue(row, rowKey));

    const ALL_SELECTED = VISIBLE_KEYS.length > 0 && VISIBLE_KEYS.every((k) => selected.includes(k));

    const SOME_SELECTED = !ALL_SELECTED && VISIBLE_KEYS.some((k) => selected.includes(k));

    const onToggleAll = () => onSelectChange?.(
        ALL_SELECTED
            ? selected.filter((k) => !VISIBLE_KEYS.includes(k))
            : [...new Set([...selected, ...VISIBLE_KEYS])],
    );

    // ---------------------------- 🔥 EXPORTACIÓN
    const EXPORT_ROWS = isServerSide ? rows : MEMO__SORTED;

    const onExport = (kind) => {

        setMenu(null);

        const NAME = `${exportFileName}_${new Date().toISOString().slice(0, 10)}`;

        if (kind === 'csv') return onExportCsv(MEMO__VISIBLE_COLUMNS, EXPORT_ROWS, `${NAME}.csv`);
        if (kind === 'excel') return onExportExcel(MEMO__VISIBLE_COLUMNS, EXPORT_ROWS, `${NAME}.xlsx`, title);
        if (kind === 'pdf') return onExportPdf(MEMO__VISIBLE_COLUMNS, EXPORT_ROWS, `${NAME}.pdf`, title);
        if (kind === 'print') return onPrintTable(MEMO__VISIBLE_COLUMNS, EXPORT_ROWS, title);
    };

    const COL_COUNT = MEMO__VISIBLE_COLUMNS.length
        + (isSelectable ? 1 : 0)
        + (rowActions ? 1 : 0)
        + (renderExpanded ? 1 : 0);

    const HIDEABLE = columns.filter((col) => col.isHideable !== false);

    /* 🔥 La barra de paginación, reutilizable arriba y abajo */
    const onRenderPagination = (place) => (
        <div className={`${BLOCK}__pagebar`} data-place={place}>

            <TablePerPage value={PER_PAGE} onChange={onPerPage}
                options={perPageOptions} totalItems={TOTAL} />

            {
                !IS_ALL && (
                    <Pagination
                        page={PAGE} total={TOTAL_PAGES} onChange={onPage}
                        showInfo totalItems={TOTAL} perPage={PER_PAGE} />
                )
            }

            {
                IS_ALL && (
                    <span className={`${BLOCK}__pagebar_all`}>
                        Mostrando los {TOTAL} registros
                    </span>
                )
            }

        </div>
    );

    const SHOW_TOP = isPaginated && (paginationPosition === 'top' || paginationPosition === 'both');
    const SHOW_BOTTOM = isPaginated && (paginationPosition === 'bottom' || paginationPosition === 'both');

    return (
        <div className={BLOCK}
            data-density={DENSITY}
            data-view={VIEW}
            data-sticky-first={isStickyFirst}
            data-rows={TEMPLATE.rows}
            data-header={TEMPLATE.header}
            data-bordered={TEMPLATE.hasBorder}
            data-hover={TEMPLATE.hasHover}
            data-shadow={TEMPLATE.hasShadow} >

            <ReactTooltip id="sd-table-tools-tooltip" place="bottom" delayShow={220} className="SDReactTooltip" />

            {/* ==================== 🔥 BARRA SUPERIOR ==================== */}
            <div className={`${BLOCK}__toolbar`} ref={REF__useRefMenus}>

                {
                    isSelectable && selected.length > 0
                        ? (
                            <div className={`${BLOCK}__bulk`}>
                                <span>{selected.length} seleccionados</span>
                                {bulkActions?.(selected)}
                                <button type="button" onClick={() => onSelectChange?.([])}>
                                    <FaTimes /> Limpiar
                                </button>
                            </div>
                        )
                        : (
                            <>
                                {
                                    isSearchable && (
                                        <div className={`${BLOCK}__search`}>
                                            <FaSearch aria-hidden="true" />
                                            <input type="search" value={SEARCH} onChange={onSearch}
                                                placeholder={searchPlaceholder}
                                                aria-label="Buscar en la tabla" />
                                            {
                                                SEARCH && (
                                                    <button type="button" aria-label="Limpiar búsqueda"
                                                        onClick={() => onSearch({ target: { value: '' } })} >
                                                        <FaTimes />
                                                    </button>
                                                )
                                            }
                                        </div>
                                    )
                                }

                                {toolbar && <div className={`${BLOCK}__toolbar_extra`}>{toolbar}</div>}

                                <div className={`${BLOCK}__tools`}>

                                    {
                                        hasViewToggle && !view && (
                                            <TableViewToggle value={VIEW} onChange={onView} />
                                        )
                                    }

                                    {
                                        isDensityToggle && VIEW === 'list' && (
                                            <button type="button" className={`${BLOCK}__tool`}
                                                title={DENSITY === 'sm' ? 'Filas normales' : 'Filas compactas'}
                                                onClick={() => setDensity(DENSITY === 'sm' ? 'md' : 'sm')} >
                                                {DENSITY === 'sm' ? <FaExpand /> : <FaCompress />}
                                            </button>
                                        )
                                    }

                                    {customTools}

                                    {
                                        isColumnToggle && HIDEABLE.length > 0 && (
                                            <div className={`${BLOCK}__menu_wrap`}>

                                                <button type="button" className={`${BLOCK}__tool`}
                                                    data-on={useMenu === 'columns'} aria-label="Columnas"
                                                    data-tooltip-id="sd-table-tools-tooltip" data-tooltip-content="Columnas"
                                                    onClick={() => setMenu(useMenu === 'columns' ? null : 'columns')} >
                                                    <FaColumns />
                                                    {useHidden.length > 0 && <span className={`${BLOCK}__tool_dot`} />}
                                                </button>

                                                {
                                                    useMenu === 'columns' && (
                                                        <div className={`${BLOCK}__menu`}>

                                                            <p className={`${BLOCK}__menu_title`}>Mostrar columnas</p>

                                                            {
                                                                HIDEABLE.map((col) => (
                                                                    <label key={col.key} className={`${BLOCK}__menu_item`}>
                                                                        <input type="checkbox"
                                                                            checked={!useHidden.includes(col.key)}
                                                                            onChange={() => onToggleColumn(col.key)} />
                                                                        <span>{col.label}</span>
                                                                    </label>
                                                                ))
                                                            }

                                                            {
                                                                useHidden.length > 0 && (
                                                                    <button type="button" className={`${BLOCK}__menu_reset`}
                                                                        onClick={() => setHidden([])} >
                                                                        Mostrar todas
                                                                    </button>
                                                                )
                                                            }

                                                        </div>
                                                    )
                                                }

                                            </div>
                                        )
                                    }

                                    {
                                        onImportClick && (
                                            <button type="button" className={`${BLOCK}__tool`}
                                                title="Importar" onClick={onImportClick} >
                                                <FaFileImport />
                                            </button>
                                        )
                                    }

                                    {
                                        isExportable && (
                                            <div className={`${BLOCK}__menu_wrap`}>

                                                <button type="button" className={`${BLOCK}__tool`}
                                                    data-on={useMenu === 'export'} aria-label="Exportar"
                                                    data-tooltip-id="sd-table-tools-tooltip" data-tooltip-content="Exportar"
                                                    onClick={() => setMenu(useMenu === 'export' ? null : 'export')} >
                                                    <FaDownload />
                                                </button>

                                                {
                                                    useMenu === 'export' && (
                                                        <div className={`${BLOCK}__menu`}>

                                                            <p className={`${BLOCK}__menu_title`}>
                                                                Guardar registros
                                                            </p>

                                                            <button type="button" className={`${BLOCK}__menu_action`}
                                                                onClick={() => onExport('excel')} >
                                                                <FaFileExcel /> Excel (.xlsx)
                                                            </button>

                                                            <button type="button" className={`${BLOCK}__menu_action`}
                                                                onClick={() => onExport('csv')} >
                                                                <FaFileCsv /> CSV
                                                            </button>

                                                            <button type="button" className={`${BLOCK}__menu_action`}
                                                                onClick={() => onExport('pdf')} >
                                                                <FaFilePdf /> PDF
                                                            </button>

                                                            <button type="button" className={`${BLOCK}__menu_action`}
                                                                onClick={() => onExport('print')} >
                                                                <FaPrint /> Imprimir
                                                            </button>

                                                        </div>
                                                    )
                                                }

                                            </div>
                                        )
                                    }

                                </div>
                            </>
                        )
                }

            </div>

            {toolbarPanel && <div className={`${BLOCK}__toolbar_panel`}>{toolbarPanel}</div>}

            {/* ==================== 🔥 PAGINACIÓN ARRIBA ==================== */}
            {SHOW_TOP && !isLoading && TOTAL > 0 && onRenderPagination('top')}

            {/* ==================== 🔥 CONTENIDO ==================== */}
            {
                isLoading
                    ? (
                        <div className={`${BLOCK}__scroll`}>
                            <table className={`${BLOCK}__table`}>
                                <tbody>
                                    {
                                        Array.from({ length: 5 }, (_, i) => (
                                            <tr key={`skeleton_${i}`}>
                                                <td><SkeletonRow columns={MEMO__VISIBLE_COLUMNS.length} /></td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    )
                    : MEMO__VISIBLE.length === 0
                        ? (
                            <EmptyState
                                icon={<FaInbox />}
                                title={SEARCH ? 'Sin resultados' : emptyTitle}
                                description={SEARCH
                                    ? `No encontramos nada para "${SEARCH}".`
                                    : emptyDescription}
                                action={SEARCH ? null : emptyAction} />
                        )
                        : VIEW === 'grid'
                            ? (
                                <TableGrid
                                    rows={MEMO__VISIBLE} columns={MEMO__VISIBLE_COLUMNS} rowKey={rowKey}
                                    renderGridItem={renderGridItem}
                                    isSelectable={isSelectable} selected={selected}
                                    onSelectChange={onSelectChange} isRowSelectable={isRowSelectable}
                                    rowActions={rowActions} onRowClick={onRowClick}
                                    columnsCount={gridColumns} />
                            )
                            : (
                                <div className={`${BLOCK}__scroll`}>

                                    <table className={`${BLOCK}__table`} data-striped={isStriped} data-sticky={isSticky}>

                                        <thead>
                                            <tr>

                                                {renderExpanded && <th className={`${BLOCK}__cell_expand`} aria-label="Detalle" />}

                                                {
                                                    isSelectable && (
                                                        <th className={`${BLOCK}__cell_check`}>
                                                            <input type="checkbox" checked={ALL_SELECTED}
                                                                ref={(el) => { if (el) el.indeterminate = SOME_SELECTED; }}
                                                                onChange={onToggleAll}
                                                                aria-label="Seleccionar todos los visibles" />
                                                        </th>
                                                    )
                                                }

                                                {
                                                    MEMO__VISIBLE_COLUMNS.map((col) => (
                                                        <th key={col.key}
                                                            style={{ width: col.width, textAlign: col.align }}
                                                            data-hide-mobile={col.isHiddenOnMobile}
                                                            aria-sort={
                                                                SORT.by === col.key
                                                                    ? (SORT.dir === 'asc' ? 'ascending' : 'descending')
                                                                    : undefined
                                                            } >

                                                            {
                                                                col.isSortable
                                                                    ? (
                                                                        <button type="button" className={`${BLOCK}__sort`}
                                                                            data-on={SORT.by === col.key}
                                                                            onClick={() => onSort(col.key)} >
                                                                            {col.label}
                                                                            {
                                                                                SORT.by !== col.key ? <FaSort />
                                                                                    : SORT.dir === 'asc' ? <FaSortUp /> : <FaSortDown />
                                                                            }
                                                                        </button>
                                                                    )
                                                                    : col.label
                                                            }

                                                        </th>
                                                    ))
                                                }

                                                {rowActions && <th className={`${BLOCK}__cell_actions`}>Acciones</th>}

                                            </tr>
                                        </thead>

                                        <tbody>
                                            {
                                                MEMO__VISIBLE.map((row) => {

                                                    const KEY = onGetValue(row, rowKey);
                                                    const IS_SELECTED = selected.includes(KEY);
                                                    const IS_EXPANDED = useExpanded.includes(KEY);
                                                    const CAN_SELECT = onIsSelectable(row);

                                                    return [

                                                        <tr key={KEY} data-selected={IS_SELECTED}
                                                            data-locked={!CAN_SELECT}
                                                            data-expanded={IS_EXPANDED}
                                                            data-clickable={!!onRowClick}
                                                            onClick={onRowClick ? (event) => {
                                                                const TARGET = event.target;
                                                                const IS_INTERACTIVE = TARGET?.closest?.(
                                                                    'button, a, input, select, textarea, label, [role="button"], [data-row-click-ignore="true"]',
                                                                );

                                                                if (IS_INTERACTIVE) return;
                                                                onRowClick(row);
                                                            } : undefined}
                                                            onKeyDown={onRowClick ? (event) => {
                                                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                                                if (event.target !== event.currentTarget) return;
                                                                event.preventDefault();
                                                                onRowClick(row);
                                                            } : undefined}
                                                            tabIndex={onRowClick ? 0 : undefined}
                                                            aria-label={onRowClick ? 'Abrir detalle del registro' : undefined} >

                                                            {
                                                                renderExpanded && (
                                                                    <td className={`${BLOCK}__cell_expand`}
                                                                        onClick={(e) => e.stopPropagation()} >
                                                                        <button type="button" className={`${BLOCK}__expand`}
                                                                            data-open={IS_EXPANDED}
                                                                            aria-label={IS_EXPANDED ? 'Ocultar detalle' : 'Ver detalle'}
                                                                            aria-expanded={IS_EXPANDED}
                                                                            onClick={() => onToggleExpand(KEY)} >
                                                                            <FaChevronRight />
                                                                        </button>
                                                                    </td>
                                                                )
                                                            }

                                                            {
                                                                isSelectable && (
                                                                    <td className={`${BLOCK}__cell_check`}
                                                                        onClick={(e) => e.stopPropagation()} >
                                                                        {
                                                                            CAN_SELECT
                                                                                ? (
                                                                                    <input type="checkbox" checked={IS_SELECTED}
                                                                                        onChange={() => onToggleRow(KEY)}
                                                                                        aria-label={`Seleccionar fila ${KEY}`} />
                                                                                )
                                                                                : (
                                                                                    <span className={`${BLOCK}__locked`} title={lockedTooltip}>
                                                                                        <FaLock />
                                                                                    </span>
                                                                                )
                                                                        }
                                                                    </td>
                                                                )
                                                            }

                                                            {
                                                                MEMO__VISIBLE_COLUMNS.map((col) => (
                                                                    <td key={col.key}
                                                                        style={{ textAlign: col.align }}
                                                                        data-hide-mobile={col.isHiddenOnMobile}
                                                                        data-label={col.label} >
                                                                        {col.render ? col.render(row) : onGetValue(row, col.key)}
                                                                    </td>
                                                                ))
                                                            }

                                                            {
                                                                rowActions && (
                                                                    <td className={`${BLOCK}__cell_actions`}
                                                                        onClick={(e) => e.stopPropagation()} >
                                                                        {rowActions(row)}
                                                                    </td>
                                                                )
                                                            }

                                                        </tr>,

                                                        /*
                                                            ⚠️ La fila expandida es una <tr> aparte, no un div
                                                               dentro de la celda. Un <div> en un <td> con
                                                               colspan rompe la alineación del resto.
                                                        */
                                                        IS_EXPANDED && renderExpanded && (
                                                            <tr key={`${KEY}_detail`} className={`${BLOCK}__row_detail`}>
                                                                <td colSpan={COL_COUNT}>{renderExpanded(row)}</td>
                                                            </tr>
                                                        ),

                                                    ].filter(Boolean);
                                                })
                                            }
                                        </tbody>

                                    </table>

                                </div>
                            )
            }

            {/* ==================== 🔥 PAGINACIÓN ABAJO ==================== */}
            {SHOW_BOTTOM && !isLoading && TOTAL > 0 && onRenderPagination('bottom')}

            {
                hasScrollTop && useShowTop && (
                    <button type="button" className={`${BLOCK}__to_top`}
                        aria-label="Volver arriba"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} >
                        <FaArrowUp />
                    </button>
                )
            }

        </div>
    );
};
